<script setup>
import { ref, computed, onMounted } from 'vue';
import { useStore, useMapGetter } from 'dashboard/composables/store';
import { useAlert } from 'dashboard/composables';
import { useI18n } from 'vue-i18n';
import { useAccount } from 'dashboard/composables/useAccount';
import { FEATURE_FLAGS } from 'dashboard/featureFlags';
import AgentBotFunctionsAPI from 'dashboard/api/agentBotFunctions';
import BotInvocationAPI from 'dashboard/api/botInvocation';
import Button from 'dashboard/components-next/button/Button.vue';
import DropdownContainer from 'next/dropdown-menu/base/DropdownContainer.vue';
import DropdownBody from 'next/dropdown-menu/base/DropdownBody.vue';
import DropdownSection from 'next/dropdown-menu/base/DropdownSection.vue';
import DropdownItem from 'next/dropdown-menu/base/DropdownItem.vue';

const props = defineProps({
  conversationId: { type: Number, required: true },
});

const store = useStore();
const { t } = useI18n();
const { accountId } = useAccount();
const isFeatureEnabledonAccount = useMapGetter(
  'accounts/isFeatureEnabledonAccount'
);

const invokeOptions = ref([]);
const isLoading = ref(false);

const currentChat = useMapGetter('getSelectedChat');

const isAgentBotsEnabled = computed(() =>
  isFeatureEnabledonAccount.value(accountId.value, FEATURE_FLAGS.AGENT_BOTS)
);

const activeBot = computed(() => {
  const meta = currentChat.value?.meta;
  return (
    meta?.assignee_agent_bot ||
    (meta?.assignee_type === 'AgentBot' ? meta?.assignee : null)
  );
});

const activeFunction = computed(
  () => currentChat.value?.meta?.active_agent_bot_function
);

const loadAllFunctions = async () => {
  isLoading.value = true;
  try {
    await store.dispatch('agentBots/get');
    const bots = (store.getters['agentBots/getBots'] || []).filter(
      bot => !bot.system_bot
    );
    const options = [];
    await Promise.all(
      bots.map(async bot => {
        try {
          const { data } = await AgentBotFunctionsAPI.getFunctions(bot.id);
          (data || [])
            .filter(fn => fn.active)
            .forEach(fn => {
              options.push({
                ...fn,
                botId: bot.id,
                botName: bot.name,
              });
            });
        } catch (_e) {
          // ignore bots without functions
        }
      })
    );
    options.sort((a, b) =>
      `${a.botName}${a.name}`.localeCompare(`${b.botName}${b.name}`)
    );
    invokeOptions.value = options;
  } catch (_e) {
    invokeOptions.value = [];
  } finally {
    isLoading.value = false;
  }
};

const refreshConversation = () =>
  store.dispatch('getConversation', props.conversationId, { root: true });

const invokeFunction = async option => {
  try {
    await BotInvocationAPI.invoke({
      conversationId: props.conversationId,
      agentBotId: option.botId,
      functionId: option.id,
    });
    useAlert(t('CONVERSATION.BOT_INVOKE.SUCCESS'));
    await refreshConversation();
  } catch (_e) {
    useAlert(t('CONVERSATION.BOT_INVOKE.ERROR'));
  }
};

const releaseBot = async () => {
  try {
    await BotInvocationAPI.release(props.conversationId);
    useAlert(t('CONVERSATION.BOT_INVOKE.RELEASE_SUCCESS'));
    await refreshConversation();
  } catch (_e) {
    useAlert(t('CONVERSATION.BOT_INVOKE.RELEASE_ERROR'));
  }
};

onMounted(loadAllFunctions);
</script>

<template>
  <DropdownContainer v-if="isAgentBotsEnabled">
    <template #trigger="{ toggle, isOpen }">
      <Button
        v-tooltip.top-end="
          activeBot
            ? $t('CONVERSATION.BOT_INVOKE.ACTIVE', {
                bot: activeBot.name,
                fn: activeFunction?.name || 'default',
              })
            : $t('CONVERSATION.BOT_INVOKE.TITLE')
        "
        icon="i-lucide-bot-message-square"
        :color="activeBot ? 'ruby' : 'slate'"
        :faded="!activeBot"
        sm
        :class="{
          'bg-n-alpha-2': isOpen && !activeBot,
          '!bg-n-ruby-9/20 !outline-n-ruby-8': activeBot && !isOpen,
        }"
        @click="toggle"
      />
    </template>
    <DropdownBody class="bottom-9 min-w-52 z-50" strong>
      <DropdownSection>
        <template v-if="activeBot">
          <DropdownItem class="cursor-default !opacity-100">
            <template #label>
              <span class="text-xs text-n-slate-11">
                {{
                  $t('CONVERSATION.BOT_INVOKE.ACTIVE', {
                    bot: activeBot.name,
                    fn: activeFunction?.name || 'default',
                  })
                }}
              </span>
            </template>
          </DropdownItem>
          <DropdownItem class="cursor-pointer" @click="releaseBot">
            <template #label>
              <span class="text-sm text-n-slate-12">
                {{ $t('CONVERSATION.BOT_INVOKE.RELEASE') }}
              </span>
            </template>
          </DropdownItem>
        </template>
        <template v-else>
          <DropdownItem v-if="isLoading" class="cursor-default !opacity-100">
            <template #label>
              <span class="text-xs text-n-slate-11">
                {{ $t('CONVERSATION.BOT_INVOKE.LOADING') }}
              </span>
            </template>
          </DropdownItem>
          <DropdownItem
            v-for="option in invokeOptions"
            :key="`${option.botId}-${option.id}`"
            class="cursor-pointer"
            @click="invokeFunction(option)"
          >
            <template #label>
              <span class="text-sm text-n-slate-12">{{ option.name }}</span>
              <span class="text-xs text-n-slate-11 line-clamp-1">
                {{ option.botName }}
                <template v-if="option.description">
                  · {{ option.description }}
                </template>
              </span>
            </template>
          </DropdownItem>
          <DropdownItem
            v-if="!invokeOptions.length && !isLoading"
            class="cursor-default !opacity-100"
          >
            <template #label>
              <span class="text-xs text-n-slate-11">
                {{ $t('CONVERSATION.BOT_INVOKE.NO_FUNCTIONS') }}
              </span>
            </template>
          </DropdownItem>
        </template>
      </DropdownSection>
    </DropdownBody>
  </DropdownContainer>
</template>
